import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner, vibrantTheme } from "../components/common";

const GROUPS = [
  { key: "guide", label: "Travel Planning Guides" },
  { key: "airline", label: "Airline Profiles & Stories" },
  { key: "airport", label: "Airport Profiles & Stories" },
  { key: null, label: "Travel & Industry" },
];

const matches = (a, q) =>
  [a.title, a.excerpt, a.category, ...(a.tags || [])].join(" ").toLowerCase().includes(q);

export default function ArticlesIndex() {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/articles-imported").then((r) => setItems(r.data.items)).catch(() => setItems(false));
  }, []);

  const q = query.trim().toLowerCase();
  const results = useMemo(() => (items && q ? items.filter((a) => matches(a, q)) : null), [items, q]);

  return (
    <div data-testid="articles-index-page">
      <PageHeader eyebrow="Library" title="Articles"
        subtitle="In-depth stories about the airlines, airports and travel trends that shape how the world flies." />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pb-14">
        {items && items.length > 0 && (
          <div className="relative max-w-xl mb-8 -mt-2" data-testid="articles-search">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="articles-search-input"
              placeholder={`Search ${items.length} guides, airlines, airports & topics…`}
              className="w-full border border-zinc-300 bg-white pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-violet-600 transition-colors" />
            {query && (
              <button onClick={() => setQuery("")} data-testid="articles-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {results && (
          <section className="mb-12" data-testid="articles-search-results">
            <h2 className="font-heading font-900 text-2xl mb-4 flex items-center gap-2">
              <span className="w-2 h-6 inline-block bg-violet-600" /> {results.length} result{results.length === 1 ? "" : "s"} for “{query.trim()}”
            </h2>
            {results.length === 0 ? (
              <p className="text-muted-foreground">Nothing matched — try a shorter word, an airline name or an airport code.</p>
            ) : (
              <ul className="divide-y divide-zinc-200 border border-zinc-300 bg-white">
                {results.slice(0, 60).map((a) => (
                  <li key={a.slug}>
                    <Link to={`/articles/${a.slug}`} data-testid={`search-result-${a.slug}`}
                      className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-violet-50 transition-colors group">
                      <span>
                        <span className="text-sm font-700 text-zinc-900 group-hover:text-violet-700">{a.title}</span>
                        {a.category && <span className="ml-2 text-xs text-muted-foreground">{a.category}</span>}
                      </span>
                      <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-violet-600 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {results.length > 60 && <p className="text-xs text-muted-foreground mt-2">Showing the first 60 — keep typing to narrow down.</p>}
          </section>
        )}

        {!results && items && items.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="articles-jump-bar">
            {(() => {
              const nTopics = items.filter((a) => ["topic", "guide", "destination"].includes(a.entity_type)).length;
              return nTopics > 0 && (
                <a href="#group-topics" data-testid="articles-jump-topics"
                  className="px-3.5 py-1.5 text-sm font-700 border border-zinc-300 bg-white hover:border-primary hover:text-primary transition-colors">
                  Traveler Guides <span className="text-muted-foreground font-mono text-xs ml-1">{nTopics}</span>
                </a>
              );
            })()}
            {GROUPS.filter((g) => g.key !== "guide").map(({ key, label }) => {
              const n = items.filter((a) => (a.entity_type || null) === key).length;
              if (!n) return null;
              return (
                <a key={label} href={`#group-${key || "general"}`} data-testid={`articles-jump-${key || "general"}`}
                  className="px-3.5 py-1.5 text-sm font-700 border border-zinc-300 bg-white hover:border-primary hover:text-primary transition-colors">
                  {label} <span className="text-muted-foreground font-mono text-xs ml-1">{n}</span>
                </a>
              );
            })}
            <a href="/visas" className="px-3.5 py-1.5 text-sm font-700 border border-zinc-300 bg-white hover:border-primary hover:text-primary transition-colors">
              Visa & Entry Guides <span className="text-muted-foreground font-mono text-xs ml-1">96</span>
            </a>
          </div>
        )}
        {items === null ? <Spinner /> : items === false ? <p className="text-muted-foreground py-10">Could not load articles.</p> : results ? null : (
          <>
            {/* Guide topics by category — compact linked titles */}
            {(() => {
              const topics = items.filter((a) => ["topic", "guide", "destination"].includes(a.entity_type));
              if (!topics.length) return null;
              const cats = [...new Set(topics.map((a) => a.category || "Travel Planning & Preparation"))].sort();
              return (
                <section id="group-topics" className="mb-14 scroll-mt-24" data-testid="articles-topics">
                  <h2 className="font-heading font-900 text-2xl mb-1 flex items-center gap-2">
                    <span className="w-2 h-6 inline-block bg-violet-600" /> Traveler Guides by Topic
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6">{topics.length} practical guides across {cats.length} categories.</p>
                  <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                    {cats.map((c) => {
                      const list = topics.filter((a) => (a.category || "Travel Planning & Preparation") === c);
                      const theme = vibrantTheme(`cat-${c}`);
                      return (
                        <div key={c} className="break-inside-avoid mb-6 border border-zinc-300 bg-white" data-testid={`topic-category-${c.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                          <div className="px-4 py-2.5 text-white font-heading font-900" style={{ backgroundColor: theme.bg }}>{c}</div>
                          <ul className="p-4 space-y-1.5">
                            {list.map((a) => (
                              <li key={a.slug}>
                                <Link to={`/articles/${a.slug}`} data-testid={`topic-link-${a.slug}`}
                                  className="text-sm text-zinc-800 hover:text-primary hover:underline transition-colors">{a.title}</Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })()}
            {GROUPS.filter((g) => g.key !== "guide").map(({ key, label }) => {
            const group = items.filter((a) => (a.entity_type || null) === key);
            if (group.length === 0) return null;
            const theme = vibrantTheme(`articles-${key || "general"}`);
            return (
              <section key={label} id={`group-${key || "general"}`} className="mb-12 scroll-mt-24" data-testid={`articles-group-${key || "general"}`}>
                <h2 className="font-heading font-900 text-2xl mb-4 flex items-center gap-2">
                  <span className="w-2 h-6 inline-block" style={{ backgroundColor: theme.bg }} /> {label}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.map((a) => (
                    <Link key={a.slug} to={`/articles/${a.slug}`} data-testid={`article-card-${a.slug}`}
                      className="group border border-zinc-300 bg-white overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
                      {a.feature_image && <img src={a.feature_image} alt="" loading="lazy" className="h-44 w-full object-cover" />}
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="font-heading font-900 text-lg text-zinc-900 group-hover:text-primary transition-colors">{a.title}</h3>
                        <p className="text-sm text-zinc-600 mt-2 line-clamp-3 flex-1">{a.excerpt}</p>
                        <span className="mt-3 text-sm font-700 inline-flex items-center gap-1.5" style={{ color: theme.bg }}>
                          Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
          </>
        )}
      </div>
    </div>
  );
}
